import React, {createContext, useContext, useState, useEffect, useMemo, useCallback} from 'react';
import {MuiThemeProvider, useMediaQuery} from '@material-ui/core';
import {lightTheme, darkTheme} from '../components/helpers/theme';
import lscache from 'lscache';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

const getInitialTheme = () => {
  const savedTheme = lscache.get('theme');
  if (savedTheme) {
    return savedTheme;
  }
  return null;
};

export function ThemeProvider({children}) {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const [theme, setTheme] = useState(() => {
    const saved = getInitialTheme();
    return saved || (prefersDarkMode ? 'dark' : 'light');
  });

  useEffect(() => {
    const savedTheme = lscache.get('theme');
    if (!savedTheme) {
      setTheme(prefersDarkMode ? 'dark' : 'light');
    }
  }, [prefersDarkMode]);

  const toggleTheme = useCallback(() => {
    setTheme(prevTheme => {
      const newTheme = prevTheme === 'light' ? 'dark' : 'light';
      lscache.set('theme', newTheme);
      return newTheme;
    });
  }, []);

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      toggleTheme,
    }),
    [theme, toggleTheme]
  );

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme === 'light' ? lightTheme : darkTheme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}
